import React, { useState, useRef, useEffect } from 'react';
import { workflows, conversations } from '../workflows/workflowData';
import { useIde } from '../../../../contexts/IdeContext';
import { IDE_OPTIONS } from '../../../../config/constants';

// Conversations imported from workflowData.js (single source of truth)

// Open in IDE dropdown component
function OpenInDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedIde, setSelectedIde } = useIde();
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = (e) => {
    e.stopPropagation();
    // Open in selected IDE
  };

  const handleSelectIde = (ide) => {
    setSelectedIde(ide);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <span style={{ color: '#9ca3af', fontSize: '11px', marginRight: '6px' }}>Open in</span>
      <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid #374151' }}>
        {/* Main button */}
        <button
          onClick={handleOpen}
          style={{
            background: '#374151',
            border: 'none',
            color: '#e5e7eb',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {selectedIde}
        </button>
        {/* Dropdown arrow */}
        <button
          onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
          style={{
            background: '#4b5563',
            border: 'none',
            borderLeft: '1px solid #6b7280',
            color: '#e5e7eb',
            padding: '4px 6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" style={{ width: '10px', height: '10px' }}>
            <path fillRule="evenodd" d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      {/* Dropdown menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '4px',
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10,
          minWidth: '140px',
          overflow: 'hidden'
        }}>
          {IDE_OPTIONS.map((ide) => (
            <button
              key={ide.name}
              onClick={() => handleSelectIde(ide.name)}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: selectedIde === ide.name ? '#374151' : 'transparent',
                border: 'none',
                color: '#e5e7eb',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textAlign: 'left'
              }}
            >
              <span>{ide.icon}</span>
              <span>{ide.name}</span>
              {selectedIde === ide.name && (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="#22c55e" style={{ width: '12px', height: '12px', marginLeft: 'auto' }}>
                  <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Code diff component - shows inline diffs like Cursor
function CodeDiff({ diffs, fileName, onHover, additions, deletions, isHighlighted }) {
  if (!diffs || diffs.length === 0) return null;

  // Determine file extension for badge
  const ext = fileName.split('.').pop().toUpperCase();
  const extColors = {
    'JS': { bg: '#fbbf24', color: '#111827' },
    'JSX': { bg: '#fbbf24', color: '#111827' },
    'TS': { bg: '#3b82f6', color: '#fff' },
    'TSX': { bg: '#3b82f6', color: '#fff' },
    'CSS': { bg: '#8b5cf6', color: '#fff' },
    'HTML': { bg: '#f97316', color: '#fff' },
    'JSON': { bg: '#6b7280', color: '#fff' }
  };
  const extStyle = extColors[ext] || { bg: '#6b7280', color: '#fff' };

  return (
    <div
      style={{
        margin: '12px 0',
        borderRadius: '8px',
        overflow: 'hidden',
        border: isHighlighted ? '2px solid #3b82f6' : '1px solid #374151',
        background: '#1f2937',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
        fontSize: '12px',
        transition: 'border-color 0.15s',
        boxShadow: isHighlighted ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none'
      }}
      onMouseEnter={() => onHover && onHover(fileName)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      {/* File header */}
      <div style={{
        padding: '8px 12px',
        background: '#111827',
        borderBottom: '1px solid #374151',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{
          background: extStyle.bg,
          color: extStyle.color,
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 600
        }}>{ext}</span>
        <span style={{ color: '#e5e7eb', flex: 1 }}>{fileName}</span>
        {/* Show additions/deletions in header */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
          {additions > 0 && (
            <span style={{ color: '#22c55e', fontWeight: 500 }}>+{additions}</span>
          )}
          {deletions > 0 && (
            <span style={{ color: '#ef4444', fontWeight: 500 }}>-{deletions}</span>
          )}
        </span>
        {/* Open in IDE dropdown */}
        <OpenInDropdown />
      </div>

      {/* Diff lines */}
      <div style={{ padding: '8px 0' }}>
        {diffs.map((diff, idx) => (
          <div key={idx}>
            {/* Old line (red/removed) */}
            {diff.oldLine && (
              <div style={{
                display: 'flex',
                background: 'rgba(239, 68, 68, 0.15)',
                borderLeft: '3px solid #ef4444'
              }}>
                <span style={{
                  minWidth: '40px',
                  padding: '2px 8px',
                  color: '#9ca3af',
                  textAlign: 'right',
                  userSelect: 'none'
                }}>{diff.lineNumber}</span>
                <pre style={{
                  margin: 0,
                  padding: '2px 12px',
                  color: '#fca5a5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}>{diff.oldLine}</pre>
              </div>
            )}
            {/* New line (green/added) */}
            {diff.newLine && (
              <div style={{
                display: 'flex',
                background: 'rgba(34, 197, 94, 0.15)',
                borderLeft: '3px solid #22c55e'
              }}>
                <span style={{
                  minWidth: '40px',
                  padding: '2px 8px',
                  color: '#9ca3af',
                  textAlign: 'right',
                  userSelect: 'none'
                }}>{diff.lineNumber}</span>
                <pre style={{
                  margin: 0,
                  padding: '2px 12px',
                  color: '#86efac',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}>{diff.newLine}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// File changes indicator - shows files that were changed
function FileChangesIndicator({ fileChanges, hoveredFile, onFileHover, expanded, onToggleExpanded }) {
  if (!fileChanges || fileChanges.length === 0) return null;

  const totalFiles = fileChanges.length;

  return (
    <div style={{
      marginTop: '12px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      background: '#fafafa'
    }}>
      {/* Header - clickable to expand */}
      <button
        onClick={onToggleExpanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '13px',
          color: '#374151'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{
              width: '16px',
              height: '16px',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s'
            }}
          >
            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
          </svg>
          <span style={{ fontWeight: 500 }}>{totalFiles} File{totalFiles > 1 ? 's' : ''}</span>
        </span>
      </button>

      {/* Expanded file list */}
      {expanded && (
        <div style={{ borderTop: '1px solid #e5e7eb' }}>
          {fileChanges.map((file, idx) => (
            <div
              key={idx}
              onMouseEnter={() => onFileHover(file.file)}
              onMouseLeave={() => onFileHover(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                borderBottom: idx < fileChanges.length - 1 ? '1px solid #f3f4f6' : 'none',
                background: hoveredFile === file.file ? '#f3f4f6' : 'transparent',
                transition: 'background 0.1s'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* File type indicator */}
                <span style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '2px 5px',
                  borderRadius: '3px',
                  background: file.type === 'added' ? '#dcfce7' : file.type === 'deleted' ? '#fee2e2' : '#fef3c7',
                  color: file.type === 'added' ? '#16a34a' : file.type === 'deleted' ? '#dc2626' : '#d97706'
                }}>
                  {file.type === 'added' ? '+' : file.type === 'deleted' ? '-' : 'M'}
                </span>
                <span style={{
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, monospace',
                  color: '#374151'
                }}>
                  {file.file}
                </span>
              </span>

              {/* Additions/Deletions */}
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                {file.additions > 0 && (
                  <span style={{ color: '#16a34a', fontWeight: 500 }}>+{file.additions}</span>
                )}
                {file.deletions > 0 && (
                  <span style={{ color: '#dc2626', fontWeight: 500 }}>-{file.deletions}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Agent selector dropdown
function AgentSelector({ selectedAgent, onSelect, isOpen, onToggle }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onToggle]);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => onToggle(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 600,
          color: '#111827'
        }}
      >
        {selectedAgent?.name || 'Select Agent'}
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
          {selectedAgent?.currentVersion}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '16px', height: '16px' }}>
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          border: '1px solid #e5e7eb',
          minWidth: '280px',
          zIndex: 100,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f3f4f6',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Select Agent
          </div>
          {workflows.map(agent => (
            <div
              key={agent.id}
              onClick={() => {
                onSelect(agent);
                onToggle(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                cursor: 'pointer',
                background: selectedAgent?.id === agent.id ? '#f9fafb' : '#fff',
                borderBottom: '1px solid #f3f4f6'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = selectedAgent?.id === agent.id ? '#f9fafb' : '#fff'}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: agent.evaluation.currentScore < 0.05 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <span style={{ fontSize: '16px' }}>🤖</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '14px', color: '#111827' }}>
                  {agent.name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {agent.description}
                </div>
              </div>
              {selectedAgent?.id === agent.id && (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#16a34a" style={{ width: '20px', height: '20px' }}>
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Response group component - groups user message with agent response
function ResponseGroup({ conversation, onHover, isHovered, hoveredFile, onFileHover }) {
  const [filesExpanded, setFilesExpanded] = useState(true);
  const { userMessage, agentResponse } = conversation;

  const fileChanges = agentResponse?.fileChanges || [];
  const allDiffs = fileChanges.flatMap(fc =>
    fc.diffs?.map(d => ({ ...d, fileName: fc.file })) || []
  );

  return (
    <div
      style={{
        marginBottom: '24px',
        borderLeft: '2px solid #e5e7eb',
        marginLeft: '20px',
        position: 'relative'
      }}
      onMouseEnter={() => onHover(conversation.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Timeline dot */}
      <div style={{
        position: 'absolute',
        left: '-5px',
        top: '16px',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: isHovered ? '#3b82f6' : '#d1d5db',
        transition: 'background 0.15s'
      }} />

      {/* User message */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '0 20px 12px 24px'
      }}>
        <div style={{
          maxWidth: '70%',
          padding: '12px 16px',
          borderRadius: '16px 16px 4px 16px',
          background: '#ef4444',
          color: '#fff',
          fontSize: '14px',
          lineHeight: 1.5
        }}>
          {userMessage.content}
        </div>
      </div>

      {/* Agent response block */}
      {agentResponse && (
        <div style={{
          padding: '0 20px 0 24px',
          position: 'relative'
        }}>
          {/* Revert button - shows on hover */}
          <button
            style={{
              position: 'absolute',
              top: '0',
              right: '20px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 500,
              color: '#6b7280',
              background: isHovered ? '#fff' : 'transparent',
              border: isHovered ? '1px solid #e5e7eb' : '1px solid transparent',
              borderRadius: '6px',
              cursor: 'pointer',
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" style={{ width: '12px', height: '12px' }}>
              <path fillRule="evenodd" d="M8 1a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 8 1ZM4.11 3.05a.75.75 0 0 1 0 1.06 5.5 5.5 0 1 0 7.78 0 .75.75 0 0 1 1.06-1.06 7 7 0 1 1-9.9 0 .75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
            </svg>
            Revert
          </button>

          {/* Agent message content */}
          <div style={{
            padding: '12px 16px',
            borderRadius: '16px',
            background: '#f3f4f6',
            color: '#111827',
            fontSize: '14px',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap'
          }}>
            {agentResponse.content}
          </div>

          {/* Inline code diffs */}
          {allDiffs.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              {fileChanges.filter(fc => fc.diffs && fc.diffs.length > 0).map((fc, idx) => (
                <CodeDiff
                  key={idx}
                  diffs={fc.diffs}
                  fileName={fc.file}
                  additions={fc.additions}
                  deletions={fc.deletions}
                  onHover={onFileHover}
                  isHighlighted={hoveredFile === fc.file}
                />
              ))}
            </div>
          )}

          {/* File changes indicator */}
          {fileChanges.length > 0 && (
            <FileChangesIndicator
              fileChanges={fileChanges}
              hoveredFile={hoveredFile}
              onFileHover={onFileHover}
              expanded={filesExpanded}
              onToggleExpanded={() => setFilesExpanded(!filesExpanded)}
            />
          )}

          {/* Timestamp and push info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '12px',
            fontSize: '11px',
            color: '#9ca3af'
          }}>
            <span>{agentResponse.time}</span>
            {agentResponse.pushNumber && (
              <>
                <span>•</span>
                <span style={{ color: '#16a34a', cursor: 'pointer' }}>Push #{agentResponse.pushNumber}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExecutionList({ searchQuery, onFileHighlight }) {
  const [selectedAgent, setSelectedAgent] = useState(workflows.find(w => w.inChat) || workflows[0]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState(conversations);
  const [hoveredConversation, setHoveredConversation] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // When hovered conversation changes, notify parent to highlight files in sidebar
  const handleConversationHover = (convId) => {
    setHoveredConversation(convId);
    if (onFileHighlight) {
      if (convId) {
        const conv = chatMessages.find(c => c.id === convId);
        const fileIds = conv?.agentResponse?.fileChanges?.map(fc => fc.fileId).filter(Boolean) || [];
        onFileHighlight(fileIds);
      } else {
        onFileHighlight([]);
      }
    }
  };

  const handleSend = () => {
    if (!chatInput.trim()) return;

    const newConversation = {
      id: `conv-${Date.now()}`,
      userMessage: {
        content: chatInput,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      },
      agentResponse: null
    };

    setChatMessages(prev => [...prev, newConversation]);
    setChatInput('');

    // Simulate agent response
    setTimeout(() => {
      setChatMessages(prev => prev.map(conv =>
        conv.id === newConversation.id
          ? {
              ...conv,
              agentResponse: {
                content: "I'll help you with that. Let me analyze the request and start working on it...",
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                fileChanges: []
              }
            }
          : conv
      ));
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      overflow: 'hidden'
    }}>
        {/* Header with Agent Selector */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <AgentSelector
            selectedAgent={selectedAgent}
            onSelect={setSelectedAgent}
            isOpen={selectorOpen}
            onToggle={setSelectorOpen}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '11px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: '#dcfce7',
              color: '#16a34a',
              fontWeight: 500
            }}>
              Connected
            </span>
            <button style={{
              padding: '6px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '16px', height: '16px' }}>
                <path fillRule="evenodd" d="M8.34 1.804A1 1 0 0 1 9.32 1h1.36a1 1 0 0 1 .98.804l.295 1.473c.497.144.971.342 1.416.587l1.25-.834a1 1 0 0 1 1.262.125l.962.962a1 1 0 0 1 .125 1.262l-.834 1.25c.245.445.443.919.587 1.416l1.473.295a1 1 0 0 1 .804.98v1.36a1 1 0 0 1-.804.98l-1.473.295a6.95 6.95 0 0 1-.587 1.416l.834 1.25a1 1 0 0 1-.125 1.262l-.962.962a1 1 0 0 1-1.262.125l-1.25-.834a6.953 6.953 0 0 1-1.416.587l-.295 1.473a1 1 0 0 1-.98.804H9.32a1 1 0 0 1-.98-.804l-.295-1.473a6.957 6.957 0 0 1-1.416-.587l-1.25.834a1 1 0 0 1-1.262-.125l-.962-.962a1 1 0 0 1-.125-1.262l.834-1.25a6.957 6.957 0 0 1-.587-1.416l-1.473-.295A1 1 0 0 1 1 10.68V9.32a1 1 0 0 1 .804-.98l1.473-.295c.144-.497.342-.971.587-1.416l-.834-1.25a1 1 0 0 1 .125-1.262l.962-.962A1 1 0 0 1 5.38 3.03l1.25.834a6.957 6.957 0 0 1 1.416-.587l.295-1.473ZM13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conversation Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: '20px'
        }}>
          {chatMessages.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6b7280'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <span style={{ fontSize: '32px' }}>🤖</span>
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                What's on the agenda today?
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
                Start a conversation with {selectedAgent?.name}
              </p>
            </div>
          ) : (
            <>
              {chatMessages.map(conv => (
                <ResponseGroup
                  key={conv.id}
                  conversation={conv}
                  onHover={handleConversationHover}
                  isHovered={hoveredConversation === conv.id}
                  hoveredFile={hoveredFile}
                  onFileHover={setHoveredFile}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Chat Input */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          background: '#fafafa'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            padding: '8px 8px 8px 16px'
          }}>
            <button style={{
              padding: '8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
              </svg>
            </button>
            <textarea
              ref={inputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything..."
              rows={1}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontSize: '14px',
                lineHeight: '20px',
                padding: '8px 0',
                background: 'transparent',
                fontFamily: 'inherit',
                maxHeight: '120px'
              }}
            />
            <button
              onClick={handleSend}
              disabled={!chatInput.trim()}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: chatInput.trim() ? '#ef4444' : '#f3f4f6',
                border: 'none',
                cursor: chatInput.trim() ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.15s'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={chatInput.trim() ? '#fff' : '#9ca3af'} style={{ width: '18px', height: '18px' }}>
                <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
              </svg>
            </button>
          </div>
          <div style={{
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: '#9ca3af'
          }}>
            <span>Using </span>
            <span style={{ fontWeight: 500, color: '#6b7280', margin: '0 4px' }}>{selectedAgent?.name}</span>
            <span> • </span>
            <span style={{ marginLeft: '4px' }}>{selectedAgent?.currentVersion}</span>
          </div>
        </div>
      </div>
  );
}

export default ExecutionList;
